import React from 'react';
import { Drawer, Descriptions, Timeline, Card, Button, Table, Space, Typography } from 'antd';
import {
  HistoryOutlined,
  PaperClipOutlined,
  CheckOutlined,
  CloseOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import ClaimStatusTag from './ClaimStatusTag';

const { Text } = Typography;

interface ClaimDetailDrawerProps {
  visible: boolean;
  onClose: () => void;
  claim: any;
  onApproveL1?: (claim: any) => void;
  onRejectL1?: (claim: any) => void;
  onApproveL2?: (claim: any) => void;
  onRejectL2?: (claim: any) => void;
  onMarkPaid?: (claim: any) => void;
  canApproveL1?: boolean;
  canApproveL2?: boolean;
  canMarkPaid?: boolean;
}

export const ClaimDetailDrawer: React.FC<ClaimDetailDrawerProps> = ({
  visible,
  onClose,
  claim,
  onApproveL1,
  onRejectL1,
  onApproveL2,
  onRejectL2,
  onMarkPaid,
  canApproveL1,
  canApproveL2,
  canMarkPaid,
}) => {
  if (!claim) return null;

  const formatMoney = (val: any) => {
    const num = Number(val);
    return !isNaN(num) && num > 0
      ? `${num.toLocaleString('vi-VN')} ${claim.currency || 'VND'}`
      : '0 VND';
  };

  return (
    <Drawer
      title={`Chi tiết Yêu cầu Claim: ${claim.claimCode}`}
      width={720}
      visible={visible}
      onClose={onClose}
      extra={
        <Space>
          {canApproveL1 && claim.status === 'SUBMITTED' && (
            <>
              <Button type="primary" icon={<CheckOutlined />} onClick={() => onApproveL1?.(claim)}>
                Duyệt L1
              </Button>
              <Button danger icon={<CloseOutlined />} onClick={() => onRejectL1?.(claim)}>
                Từ chối L1
              </Button>
            </>
          )}

          {canApproveL2 && claim.status === 'APPROVED_BY_APPROVER' && (
            <>
              <Button type="primary" icon={<CheckOutlined />} onClick={() => onApproveL2?.(claim)}>
                Finance Duyệt
              </Button>
              <Button danger icon={<CloseOutlined />} onClick={() => onRejectL2?.(claim)}>
                Finance Từ chối
              </Button>
            </>
          )}

          {canMarkPaid && claim.status === 'APPROVED_BY_FINANCE' && (
            <Button
              type="primary"
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
              icon={<DollarOutlined />}
              onClick={() => onMarkPaid?.(claim)}
            >
              Xác nhận Thanh toán
            </Button>
          )}
        </Space>
      }
    >
      <Descriptions title="Thông tin Yêu cầu Chi tiết" bordered column={2} size="small">
        <Descriptions.Item label="Mã Claim">{claim.claimCode}</Descriptions.Item>
        <Descriptions.Item label="Trạng thái">
          <ClaimStatusTag status={claim.status} />
        </Descriptions.Item>
        <Descriptions.Item label="Tiêu đề" span={2}>
          <Text style={{ fontWeight: 'bold', fontSize: 15 }}>{claim.title}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Số tiền thanh toán">
          <Text type="danger" style={{ fontSize: 16, fontWeight: 'bold' }}>
            {formatMoney(claim.amount)}
          </Text>
        </Descriptions.Item>
        <Descriptions.Item label="Ngày phát sinh chi phí">
          {claim.claimDate ? moment(claim.claimDate).format('DD/MM/YYYY') : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Loại chi phí">
          {claim.expenseType?.nameVi || claim.expenseType?.code || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Phương thức thanh toán">
          {claim.paymentMethod?.nameVi || claim.paymentMethod?.code || '-'}
        </Descriptions.Item>

        {claim.paymentMethod?.code === 'BANK_TRANSFER' && (
          <>
            <Descriptions.Item label="Ngân hàng thụ hưởng">
              {claim.bank ? `${claim.bank.code} - ${claim.bank.name}` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Số tài khoản">
              {claim.targetAccountNumber || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Tên người thụ hưởng" span={2}>
              {claim.beneficiaryName || '-'}
            </Descriptions.Item>
          </>
        )}

        <Descriptions.Item label="CC Email thông báo" span={2}>
          {claim.ccEmails ? (
            <Text code>{claim.ccEmails}</Text>
          ) : (
            <Text type="secondary">Không có CC email</Text>
          )}
        </Descriptions.Item>

        <Descriptions.Item label="Mô tả chi tiết" span={2}>
          {claim.description || 'Không có mô tả'}
        </Descriptions.Item>

        {claim.paymentNote && (
          <Descriptions.Item label="Ghi chú thanh toán (Finance)" span={2}>
            <Text type="warning">{claim.paymentNote}</Text>
          </Descriptions.Item>
        )}
      </Descriptions>

      {/* File đính kèm */}
      <Card
        title={
          <>
            <PaperClipOutlined /> File chứng từ / Hóa đơn
          </>
        }
        size="small"
        style={{ marginTop: 16 }}
      >
        {claim.attachments && claim.attachments.length > 0 ? (
          <Table
            dataSource={claim.attachments}
            rowKey="id"
            pagination={false}
            size="small"
            columns={[
              { title: 'Tên file', dataIndex: 'fileName', key: 'fileName' },
              {
                title: 'Dung lượng',
                dataIndex: 'sizeBytes',
                key: 'sizeBytes',
                render: (sz: number) => (sz ? `${(sz / 1024).toFixed(1)} KB` : '-'),
              },
              {
                title: 'Thao tác',
                key: 'action',
                render: (_, record: any) => (
                  <Button
                    type="link"
                    size="small"
                    href={`/api/costs-claim/download/${record.s3Key}`}
                    target="_blank"
                  >
                    Tải về
                  </Button>
                ),
              },
            ]}
          />
        ) : (
          <Text type="secondary">Chưa có file đính kèm nào</Text>
        )}
      </Card>

      {/* Audit Log Timeline */}
      <Card
        title={
          <>
            <HistoryOutlined /> Lịch sử xử lý & Audit Log
          </>
        }
        size="small"
        style={{ marginTop: 16 }}
      >
        {claim.histories && claim.histories.length > 0 ? (
          <Timeline mode="left" style={{ marginTop: 8 }}>
            {claim.histories.map((h: any) => (
              <Timeline.Item
                key={h.id}
                color={
                  h.actionType.includes('REJECT')
                    ? 'red'
                    : h.actionType.includes('APPROVE') || h.actionType.includes('PAID')
                    ? 'green'
                    : 'blue'
                }
              >
                <div style={{ fontWeight: 'bold' }}>{h.actionType}</div>
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                  Bởi: {h.actorName || h.actorId || h.createdBy} -{' '}
                  {moment(h.createdAt).format('DD/MM/YYYY HH:mm')}
                </div>
                {h.note && (
                  <div style={{ fontSize: 13, fontStyle: 'italic', marginTop: 4 }}>"{h.note}"</div>
                )}
              </Timeline.Item>
            ))}
          </Timeline>
        ) : (
          <Text type="secondary">Chưa có lịch sử</Text>
        )}
      </Card>
    </Drawer>
  );
};

export default ClaimDetailDrawer;
